import { AuditOutlined } from "@ant-design/icons";
import { Alert, Input, Modal, Radio, Space, Spin, Tag, Typography, message } from "antd";
import { useAppDispatch, useAppSelector } from "hooks";
import React from "react";
import { getStewardConflictsRequest, stewardResolveRequest } from "store/stewardship/actions";

// Stewardship (PRE-2287) — a steward resolves the field conflicts the dedup merge left
// undecided: pick the winning value per conflicted field (or type one) with a mandatory
// audit reason. The resolution is written onto the surviving record with MANUAL provenance.

interface IStewardResolveModalProps {
    open: boolean;
    onClose: () => void;
    survivor?: IDataObjectRecord | null;
    // Fired after a successful resolve so the host view can clear its review banner.
    onResolved?: () => void;
}

export default function StewardResolveModal({ open, onClose, survivor, onResolved }: IStewardResolveModalProps) {
    const dispatch = useAppDispatch();
    const fieldConflicts = useAppSelector((s) => s.stewardship?.fieldConflicts) || [];
    const conflictsLoading = useAppSelector((s) => s.stewardship?.conflictsLoading);
    const getConflictsResult = useAppSelector((s) => s.stewardship?.getConflictsResult);
    const resolving = useAppSelector((s) => s.stewardship?.resolving);
    const resolveResult = useAppSelector((s) => s.stewardship?.resolveResult);

    const [resolutions, setResolutions] = React.useState<Record<string, unknown>>({});
    const [reason, setReason] = React.useState("");
    const wasResolving = React.useRef(false);

    React.useEffect(() => {
        if (open && survivor) {
            setResolutions({});
            setReason("");
            dispatch(getStewardConflictsRequest(survivor._duplicateSetId, survivor._duplicateSetVersion, survivor._groupId));
        }
    }, [open, survivor, dispatch]);

    React.useEffect(() => {
        if (wasResolving.current && !resolving) {
            if (resolveResult?.success) {
                message.success("Conflicts resolved — the surviving record has been updated.");
                onResolved && onResolved();
                onClose();
            } else {
                message.error(resolveResult?.message || "Failed to resolve the conflicts.");
            }
        }
        wasResolving.current = !!resolving;
    }, [resolving, resolveResult, onClose, onResolved]);

    // Preferred source: the merge preview's conflicts (value candidates included).
    // Fallback when unavailable: the flagged fields stored on the cluster record, free-typed.
    const conflicts =
        fieldConflicts.length > 0
            ? fieldConflicts
            : (survivor?.__stewardReviewFields || []).map((field) => ({ ...field, values: [] as { value: unknown; dataObjectId?: string; dataSetId?: string }[] }));

    const submit = () => {
        if (!survivor) {
            return;
        }
        const chosen = Object.fromEntries(Object.entries(resolutions).filter(([, value]) => value !== undefined && value !== null && value !== ""));
        if (Object.keys(chosen).length === 0) {
            message.warning("Pick or enter at least one winning value.");
            return;
        }
        if (!reason.trim()) {
            message.warning("A reason is required for audit.");
            return;
        }
        dispatch(
            stewardResolveRequest({
                duplicateSetId: survivor._duplicateSetId,
                duplicateSetVersion: survivor._duplicateSetVersion,
                groupId: survivor._groupId,
                dataObjectId: survivor._dataObjectId,
                resolutions: chosen,
                reason: reason.trim(),
            }),
        );
    };

    return (
        <Modal
            title={
                <span className="inline-flex items-center gap-2">
                    <AuditOutlined className="text-orange-500" />
                    Resolve steward review
                </span>
            }
            open={open}
            onCancel={onClose}
            onOk={submit}
            okText="Resolve"
            confirmLoading={!!resolving}
            width={640}
            data-testid="steward-resolve-modal"
        >
            <Space direction="vertical" className="w-full" size="middle">
                <Alert
                    type="warning"
                    showIcon
                    message="These fields had conflicting values the merge rules could not decide."
                    description="Your picks are written onto the surviving record with manual provenance (who, when, why) and win future merge runs."
                />
                {getConflictsResult && !getConflictsResult.success && (
                    <Alert
                        type="error"
                        showIcon
                        message="Could not load the candidate values"
                        description="Showing the flagged fields for manual entry instead — values you type are used as-is."
                        data-testid="steward-conflicts-load-error"
                    />
                )}
                {conflictsLoading ? (
                    <div className="flex justify-center py-6">
                        <Spin />
                    </div>
                ) : conflicts.length === 0 ? (
                    <Typography.Text type="secondary">No unresolved conflicts were reported for this cluster.</Typography.Text>
                ) : (
                    conflicts.map((conflict) => (
                        <div key={conflict.fieldId} className="rounded border border-gray-200 p-3" data-testid="steward-conflict-field">
                            <Typography.Text strong>{conflict.fieldName}</Typography.Text>
                            {conflict.values.length > 0 ? (
                                <Radio.Group
                                    className="mt-2 flex flex-col gap-1"
                                    value={resolutions[conflict.fieldId]}
                                    onChange={(event) => setResolutions((prev) => ({ ...prev, [conflict.fieldId]: event.target.value }))}
                                >
                                    {conflict.values.map((candidate, index) => (
                                        <Radio key={`${conflict.fieldId}-${index}`} value={candidate.value}>
                                            {String(candidate.value)}
                                            {candidate.dataSetId && (
                                                <Tag className="ml-2" color="blue">
                                                    {candidate.dataSetId.slice(-6)}
                                                </Tag>
                                            )}
                                        </Radio>
                                    ))}
                                </Radio.Group>
                            ) : (
                                <Input
                                    className="mt-2"
                                    placeholder="Enter the winning value"
                                    value={(resolutions[conflict.fieldId] as string) || ""}
                                    onChange={(event) => setResolutions((prev) => ({ ...prev, [conflict.fieldId]: event.target.value }))}
                                />
                            )}
                        </div>
                    ))
                )}
                <div>
                    <Typography.Text strong>Reason (required — documented for audit)</Typography.Text>
                    <Input.TextArea
                        rows={2}
                        className="mt-1"
                        value={reason}
                        placeholder="Why these values win…"
                        onChange={(event) => setReason(event.target.value)}
                        data-testid="steward-resolve-reason"
                    />
                </div>
            </Space>
        </Modal>
    );
}
